import React, { useState } from 'react';
import { css } from '@emotion/react';
import { Grid } from '@material-ui/core';

import { DESIGN_SCOPE_CLASS } from '@onward-delivery/design/scope';

import { ResponsiveSidebarDialog, ModalTitle, ModalContent } from '@/styles/blocks';
import { OnwardTabContainer, OnwardTab } from '@/components/Tabs';

import { Body2, Header2, SectionHeader, BORDER, GREY_TEXT } from '../blocks';
import { Meta, PastResourceCard } from '../calendarBlocks';
import {
    GUIDE_TABS,
    GUIDE_TAB_LABELS,
    GUIDE_TAB_ORDER,
    PAST_CARD_EXAMPLES,
    PAST_CARD_INTRO,
    PAST_DAY_GUIDE,
    PAST_DAY_MARKERS,
    RESOURCE_CARD_GUIDE,
    UPCOMING_DAY_GUIDE,
} from '../statusGuide';

const GuideEntry = ({ term, statuses, blurb }) => (
    <Grid
        container
        direction="column"
        css={css`
            padding: 12px 0;
            border-bottom: 1px solid ${BORDER};

            :last-of-type {
                border-bottom: none;
            }
        `}
    >
        <Grid
            container
            direction="row"
            css={css`
                align-items: baseline;
                flex-wrap: wrap;
                gap: 8px;
            `}
        >
            <Body2
                css={css`
                    font-weight: 700;
                `}
            >
                {term}
            </Body2>
            {/* The statuses behind the label, so the reader can match it to an invoice. */}
            {statuses?.length ? <Meta>{statuses.join(' · ')}</Meta> : null}
        </Grid>
        <Body2
            css={css`
                margin-top: 2px;
                color: ${GREY_TEXT};
            `}
        >
            {blurb}
        </Body2>
    </Grid>
);

// A card sample at the width a real one renders at, so the swatch reads at true size.
const CardExample = ({ state, name, blurb }) => (
    <Grid
        container
        direction="row"
        css={css`
            align-items: center;
            flex-wrap: nowrap;
            gap: 14px;
            margin-bottom: 10px;
        `}
    >
        <div
            css={css`
                flex: 0 0 150px;
                pointer-events: none;
            `}
        >
            <PastResourceCard resource={{ name }} billingState={state} />
        </div>
        <Body2
            css={css`
                color: ${GREY_TEXT};
            `}
        >
            {blurb}
        </Body2>
    </Grid>
);

const Section = ({ title, first, children }) => (
    <>
        <SectionHeader
            css={css`
                ${first ? '' : 'margin-top: 24px;'}
            `}
        >
            {title}
        </SectionHeader>
        {children}
    </>
);

// Reference only — no data, so its open state lives with the button that opens it.
//
// `showResourceCards` rather than the calendar's own layout name: what decides whether the card
// sections belong is whether the cells render cards, and only the week ones do.
const StatusGuideModal = ({ open, onClose, showResourceCards = false }) => {
    const [tab, setTab] = useState(GUIDE_TABS.PAST);

    const close = () => {
        setTab(GUIDE_TABS.PAST);
        onClose();
    };

    return (
        <ResponsiveSidebarDialog open={open} onClose={close} maxWidth="sm" fullWidth>
            <ModalTitle onClose={close}>
                <Header2
                    css={css`
                        color: #4c4c4c;
                    `}
                >
                    What the calendar is telling you
                </Header2>
            </ModalTitle>
            <OnwardTabContainer
                value={tab}
                onChange={(_, next) => setTab(next)}
                css={css`
                    height: auto;
                    min-height: 48px;
                    border-bottom: 1px solid ${BORDER};
                    padding: 0 16px;

                    .MuiTab-root {
                        height: 48px;
                        min-height: 48px;
                        padding: 12px 16px;
                    }
                `}
            >
                {GUIDE_TAB_ORDER.map((key) => (
                    <OnwardTab key={key} value={key} label={GUIDE_TAB_LABELS[key]} />
                ))}
            </OnwardTabContainer>
            {/* Portalled outside the Calendar's scope, so the sample cards need their own. */}
            <ModalContent
                className={DESIGN_SCOPE_CLASS}
                css={css`
                    width: 100%;
                `}
            >
                {tab === GUIDE_TABS.PAST ? (
                    <>
                        {showResourceCards ? (
                            <Section title="CARDS" first>
                                <Body2
                                    css={css`
                                        color: ${GREY_TEXT};
                                        padding: 8px 0 4px;
                                    `}
                                >
                                    {PAST_CARD_INTRO}
                                </Body2>
                                {PAST_CARD_EXAMPLES.map((example) => (
                                    <CardExample key={example.state} {...example} />
                                ))}
                            </Section>
                        ) : null}
                        <Section title="BILLING" first={!showResourceCards}>
                            {PAST_DAY_GUIDE.map((entry) => (
                                <GuideEntry key={entry.term} {...entry} />
                            ))}
                        </Section>
                        <Section title="Flags">
                            {PAST_DAY_MARKERS.map((entry) => (
                                <GuideEntry key={entry.term} {...entry} />
                            ))}
                        </Section>
                    </>
                ) : (
                    <>
                        <Section title="KPIs & WORDING" first>
                            {UPCOMING_DAY_GUIDE.map((entry) => (
                                <GuideEntry key={entry.term} {...entry} />
                            ))}
                        </Section>
                        {showResourceCards ? (
                            <Section title="EACH RESOURCE">
                                {RESOURCE_CARD_GUIDE.map((entry) => (
                                    <GuideEntry key={entry.term} {...entry} />
                                ))}
                            </Section>
                        ) : null}
                    </>
                )}
            </ModalContent>
        </ResponsiveSidebarDialog>
    );
};

export default StatusGuideModal;
